import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as ClearIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import { CAMPAIGN_TYPES, getCampaignTypes } from "./CampaignDetailPanel";

const CAMPAIGN_STATUS_STYLE = {
  draft:      { color: "#475569", bg: "#F1F5F9", border: "#CBD5E1" },
  active:     { color: "#065F46", bg: "#D1FAE5", border: "#10B981" },
  processing: { color: "#1E40AF", bg: "#DBEAFE", border: "#3B82F6" },
  suspended:  { color: "#92400E", bg: "#FEF3C7", border: "#F59E0B" },
  stopped:    { color: "#7F1D1D", bg: "#FEE2E2", border: "#EF4444" },
  rescinded:  { color: "#7F1D1D", bg: "#FEE2E2", border: "#EF4444" },
};

const CampaignListPanel = ({
  onClose,
  campaigns = [],
  selectedId,
  searchValue = "",
  onSearchChange,
  onSearchSubmit,
  onSelect,
  onAdd,
  onActivate,
  onStop,
  onSend,
  onDelete,
  onCopy,
}) => {
  const [typeFilter, setTypeFilter] = useState("");
  // Campaign pending stop confirmation; Stop only fires after the user confirms.
  const [confirmStop, setConfirmStop] = useState(null);
  // Campaign pending delete confirmation; Delete only fires after the user confirms.
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Campaign pending send confirmation; Send only fires after the user confirms.
  const [confirmSend, setConfirmSend] = useState(null);
  const rows = useMemo(() => {
    const base = Array.isArray(campaigns) ? campaigns : [];
    if (!typeFilter) return base;
    return base.filter(
      (c) => getCampaignTypes(c).includes(typeFilter),
    );
  }, [campaigns, typeFilter]);
  // A campaign is sendable as long as it has a domain. Empty filter
  // means "the whole domain"; presence of any chip narrows it down.
  const hasUsableFilter = (campaign) => Boolean(String(campaign?.domain || "").trim());

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", gap: 0.5, flex: 1, alignItems: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search campaigns..."
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSearchSubmit?.(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange?.("")} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 28,
                fontSize: 12,
                borderRadius: 1,
              },
            }}
          />
          <TextField
            select
            size="small"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{
              minWidth: 140,
              "& .MuiOutlinedInput-root": {
                height: 28,
                fontSize: 11,
                borderRadius: 1,
              },
            }}
          >
            <MenuItem value=""><em>All types</em></MenuItem>
            {CAMPAIGN_TYPES.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: 11 }}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      }
      headerActions={(
        <IconButton
          size="small"
          onClick={onAdd}
          title="Add campaign"
          data-shell-raw="true"
          sx={{
            borderRadius: "50%",
            backgroundColor: "#16a34a",
            "&:hover": { backgroundColor: "#15803d" },
            width: 24,
            height: 24,
            p: 0,
          }}
        >
          <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
        </IconButton>
      )}
      collectionLabel="Campaigns"
      footerLeft={<Typography sx={{ color: "text.secondary", fontSize: 11 }}>{rows.length} loaded</Typography>}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {rows.map((campaign) => {
          const isSelected = campaign?.id === selectedId;
          const filterReady = hasUsableFilter(campaign);
          const saved = Boolean(campaign?.id) && !campaign?._isNew;
          const status = String(campaign?.status || "").toLowerCase();
          const canActivate = saved && filterReady && !["active", "processing"].includes(status);
          const canStop = saved && ["active", "processing"].includes(status);
          // Drafts delete directly; activated campaigns must be stopped first.
          // Deletable: drafts, suspended, and completed (sent) campaigns.
          // Only a live/in-progress campaign (active/processing) must be stopped first.
          const canDelete = saved && !["active", "processing"].includes(status);
          return (
            <Box
              key={campaign?.id || `campaign-${campaign?.name || ""}`}
              onClick={() => onSelect?.(campaign)}
              sx={{
                border: "1px solid",
                borderColor: isSelected ? "#EAB308" : "#E5E7EB",
                borderRadius: "10px",
                p: 1,
                cursor: "pointer",
                backgroundColor: isSelected ? "rgba(234, 179, 8, 0.08)" : "#FFFFFF",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <Typography
                  noWrap
                  sx={{ fontSize: 13, fontWeight: 700, color: "#111827", flex: 1, minWidth: 0 }}
                >
                  {campaign?.name || "Untitled Campaign"}
                </Typography>
                {(() => {
                  const statusLabel = campaign?.status || "Draft";
                  const sty = CAMPAIGN_STATUS_STYLE[String(statusLabel).toLowerCase()] || CAMPAIGN_STATUS_STYLE.draft;
                  return (
                    <Box
                      sx={{
                        flexShrink: 0,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: "999px",
                        border: `1px solid ${sty.border}`,
                        color: sty.color,
                        backgroundColor: sty.bg,
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 1.4,
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {statusLabel}
                    </Box>
                  );
                })()}
              </Box>
              <Box
                sx={{
                  mt: 0.7,
                  pt: 0.55,
                  borderTop: "1px solid rgba(148,163,184,0.22)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 0.75,
                }}
              >
                {getCampaignTypes(campaign).length ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
                    {getCampaignTypes(campaign).map((t) => (
                      <Box
                        key={t}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 0.75,
                          py: 0.125,
                          borderRadius: "999px",
                          border: "1px solid #C7D2FE",
                          backgroundColor: "#EEF2FF",
                          color: "#3730A3",
                          fontSize: 10,
                          fontWeight: 600,
                          lineHeight: 1.4,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {t}
                      </Box>
                    ))}
                  </Box>
                ) : <Box />}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Tooltip
                  title={
                    canStop
                      ? "Active — switch off to stop"
                      : canActivate
                        ? "Activate campaign"
                        : "Save the campaign to activate it"
                  }
                >
                  <span>
                    <Switch
                      size="small"
                      checked={canStop}
                      disabled={!canStop && !canActivate}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        if (e.target.checked) onActivate?.(campaign);
                        else setConfirmStop(campaign);
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#10B981" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#10B981" },
                      }}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={canStop ? "Send campaign" : "Activate the campaign to send"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmSend(campaign);
                      }}
                      disabled={!canStop}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: canStop ? "#3F51B5" : "#F1F5F9",
                        color: canStop ? "#FFFFFF" : "#94A3B8",
                        "&:hover": { backgroundColor: canStop ? "#3949AB" : "#F1F5F9" },
                      }}
                    >
                      <SendIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                {/* Copy temporarily disabled: createCampaign copy fails with a
                    network error under investigation. Re-enable once fixed. */}
                {false && (
                <Tooltip title="Copy to a new draft">
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy?.(campaign);
                      }}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: "#EEF2FF",
                        color: "#4338CA",
                        "&:hover": { backgroundColor: "#E0E7FF" },
                      }}
                    >
                      <CopyIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                )}
                                <Tooltip title="Delete campaign">
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(campaign);
                      }}
                      disabled={!canDelete}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: canDelete ? "#FEF2F2" : "#F1F5F9",
                        color: canDelete ? "#DC2626" : "#94A3B8",
                        "&:hover": { backgroundColor: canDelete ? "#FEE2E2" : "#F1F5F9" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                </Box>
              </Box>
            </Box>
          );
        })}
        {rows.length === 0 && (
          <Typography sx={{ fontSize: 12, color: "#64748B", textAlign: "center", py: 3 }}>
            No campaigns found.
          </Typography>
        )}
      </Box>
      <Dialog open={Boolean(confirmStop)} onClose={() => setConfirmStop(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: "#1A1F36" }}>
          Stop this campaign?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#1A1F36" }}>
            Are you sure you want to stop &quot;{confirmStop?.name || "this campaign"}&quot;?
            Sending will halt and its status will change to Suspended. You can activate it again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setConfirmStop(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              onStop?.(confirmStop);
              setConfirmStop(null);
            }}
            sx={{
              textTransform: "none",
              backgroundColor: "#DC2626",
              "&:hover": { backgroundColor: "#B91C1C" },
            }}
          >
            Stop campaign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmSend)} onClose={() => setConfirmSend(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: "#1A1F36" }}>
          Send this campaign?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#1A1F36" }}>
            This sends &quot;{confirmSend?.name || "this campaign"}&quot; to every matching contact.
            Real emails go out if sending is enabled on the server.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setConfirmSend(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              onSend?.(confirmSend);
              setConfirmSend(null);
            }}
            sx={{
              textTransform: "none",
              backgroundColor: "#3F51B5",
              "&:hover": { backgroundColor: "#3949AB" },
            }}
          >
            Send campaign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Delete campaign</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Are you sure you want to delete &quot;{confirmDelete?.name || "this campaign"}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmDelete(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={() => {
              onDelete?.(confirmDelete);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AddressbookPanelShell>
  );
};

export default CampaignListPanel;
